import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { establishmentsApi, dataApi } from '@/lib/api';
import { Save, CheckCircle, AlertCircle, Plus, Building2 } from 'lucide-react';

const RESTAURANT_FIELDS = [
  { key: 'ca_ht', label: "Chiffre d'affaires HT", unit: '€', placeholder: '89000' },
  { key: 'nb_clients', label: 'Nombre de clients (couverts)', unit: '', placeholder: '2340', type: 'int' },
  { key: 'jours_ouverture', label: "Jours d'ouverture du mois", unit: 'jours', placeholder: '26', type: 'int' },
  { key: 'cout_matieres', label: 'Coût matières premières HT', unit: '€', placeholder: '28480' },
  { key: 'cout_personnel', label: 'Coût du personnel', unit: '€', placeholder: '33820' },
  { key: 'stock_moyen', label: 'Valeur moyenne de stock', unit: '€', placeholder: '9200' },
  { key: 'cout_energie', label: 'Coût énergie', unit: '€', placeholder: '3560' },
  { key: 'impots_taxes', label: 'Impôts & taxes', unit: '€', placeholder: '1780' },
  { key: 'commissions_plateformes', label: 'Commissions plateformes', unit: '€', placeholder: '890' },
];

const PHARMACY_FIELDS = [
  { key: '_group_ca', group: true, label: "Chiffre d'affaires" },
  { key: 'ca_ht', label: "CA HT total du mois", unit: '\u20ac', placeholder: '150000', required: true },
  { key: 'ca_remboursable_ht', label: 'Dont CA remboursable HT', unit: '\u20ac', placeholder: '115000', indent: true },
  { key: 'ca_parapharmacie', label: 'Dont CA parapharmacie HT', unit: '\u20ac', placeholder: '35000', indent: true },
  { key: 'ca_otc', label: 'Dont CA vente libre / OTC HT', unit: '\u20ac', placeholder: '12000', indent: true },
  { key: '_group_charges', group: true, label: 'Achats & Charges' },
  { key: 'achats_marchandises', label: 'Achats marchandises HT', unit: '\u20ac', placeholder: '98000', required: true },
  { key: 'cout_personnel', label: 'Co\u00fbt total personnel', unit: '\u20ac', placeholder: '23000', required: true },
  { key: 'charges_externes', label: 'Charges externes', unit: '\u20ac', placeholder: '14000', required: true },
  { key: '_group_activite', group: true, label: 'Activit\u00e9 & Ordonnances' },
  { key: 'jours_ouverture', label: "Jours d'ouverture", unit: 'jours', placeholder: '26', type: 'int', required: true },
  { key: 'nb_clients', label: 'Nombre de clients / tickets', unit: '', placeholder: '4200', type: 'int', required: true },
  { key: 'nb_ordonnances', label: "Ordonnances d\u00e9livr\u00e9es", unit: '', placeholder: '5500', type: 'int' },
  { key: 'nb_ordonnances_renouvellement', label: 'Dont renouvellements', unit: '', placeholder: '3200', type: 'int', indent: true },
  { key: 'nb_lignes_dispensees', label: 'Lignes dispens\u00e9es', unit: '', placeholder: '12000', type: 'int' },
  { key: '_group_prod', group: true, label: 'Productivit\u00e9' },
  { key: 'heures_travaillees_equipe', label: 'Heures travaill\u00e9es \u00e9quipe', unit: 'h', placeholder: '680' },
  { key: '_group_stock', group: true, label: 'Stock' },
  { key: 'stock_debut_mois_ht', label: 'Stock d\u00e9but de mois', unit: '\u20ac', placeholder: '52000' },
  { key: 'stock_moyen', label: 'Stock moyen', unit: '\u20ac', placeholder: '55000' },
  { key: '_group_tp', group: true, label: 'Tr\u00e9sorerie & Tiers Payant' },
  { key: 'tresorerie_disponible', label: 'Tr\u00e9sorerie disponible', unit: '\u20ac', placeholder: '30000' },
  { key: 'montant_tiers_payant_soumis', label: 'TP soumis', unit: '\u20ac', placeholder: '95000' },
  { key: 'montant_tiers_payant_rejete', label: 'Dont TP rejet\u00e9', unit: '\u20ac', placeholder: '3200', indent: true },
  { key: 'montant_tiers_payant_attente', label: 'TP en attente', unit: '\u20ac', placeholder: '18000' },
  { key: '_group_ctx', group: true, label: 'Contextuel' },
  { key: 'taux_generiques', label: 'Taux substitution g\u00e9n\u00e9riques', unit: '%', placeholder: '85.0' },
];

const MONTHS = [
  { value: '01', label: 'Janvier' }, { value: '02', label: 'Février' }, { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' }, { value: '05', label: 'Mai' }, { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' }, { value: '08', label: 'Août' }, { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' }, { value: '11', label: 'Novembre' }, { value: '12', label: 'Décembre' },
];

export default function DataEntryPage() {
  const [searchParams] = useSearchParams();
  const preselectedId = searchParams.get('establishment');
  const [establishments, setEstablishments] = useState([]);
  const [selectedEst, setSelectedEst] = useState(preselectedId || '');
  const [period, setPeriod] = useState(`2026-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [existingData, setExistingData] = useState([]);

  useEffect(() => {
    establishmentsApi.list().then(r => {
      // Show ALL establishments — admin needs to enter data for any
      setEstablishments(r.data);
      if (!selectedEst && r.data.length > 0) {
        // Prefer non-demo first
        const nonDemo = r.data.filter(x => !x.is_demo);
        setSelectedEst(nonDemo.length > 0 ? nonDemo[0].id : r.data[0].id);
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (selectedEst) {
      dataApi.list(selectedEst).then(r => {
        setExistingData(r.data);
        const existing = r.data.find(d => d.period === period);
        if (existing) setFormData(existing.data);
        else setFormData({});
      });
    }
  }, [selectedEst, period]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentFields = (establishments.find(e => e.id === selectedEst)?.sector_slug === 'pharmacies') ? PHARMACY_FIELDS : RESTAURANT_FIELDS;
      const payload = {};
      currentFields.filter(f => !f.group).forEach(f => {
        const val = formData[f.key] || 0;
        payload[f.key] = f.type === 'int' ? parseInt(val) : parseFloat(val);
      });
      await dataApi.submit(selectedEst, { period, data: payload });
      setSaved(true);
      const r = await dataApi.list(selectedEst);
      setExistingData(r.data);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const year = period.split('-')[0];
  const month = period.split('-')[1];

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-[#1B2A4A]/20 border-t-[#1B2A4A] rounded-full animate-spin" /></div>;

  if (establishments.length === 0) {
    return (
      <div className="space-y-6" data-testid="data-entry-page">
        <div>
          <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Saisie des données</h1>
          <p className="text-gray-400 mt-1 text-sm">Entrez les indicateurs mensuels de l'établissement</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Building2 size={40} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Aucun établissement</p>
          <p className="text-gray-400 text-sm mt-1 mb-4">Créez d'abord un établissement pour pouvoir saisir ses données</p>
          <Link to="/clients" className="inline-flex items-center gap-2 bg-[#1B2A4A] text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#152039] shadow-md" data-testid="data-entry-create-link">
            <Plus size={16} /> Créer un établissement
          </Link>
        </div>
      </div>
    );
  }

  const selectedEstObj = establishments.find(e => e.id === selectedEst);
  const isPharmacy = selectedEstObj?.sector_slug === 'pharmacies';
  const FIELDS = isPharmacy ? PHARMACY_FIELDS : RESTAURANT_FIELDS;

  return (
    <div className="space-y-6" data-testid="data-entry-page">
      <div>
        <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Saisie des données</h1>
        <p className="text-gray-400 mt-1 text-sm">Entrez les indicateurs mensuels de l'établissement</p>
      </div>

      <div className="bg-white rounded-xl shadow-[0_2px_8px_rgba(27,42,74,0.04)] border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Établissement</label>
            <select
              data-testid="data-entry-establishment"
              value={selectedEst}
              onChange={e => setSelectedEst(e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]"
            >
              {establishments.map(e => (
                <option key={e.id} value={e.id}>
                  {e.name}{e.city ? ` — ${e.city}` : ''}{e.is_demo ? ' (cohorte)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Mois</label>
            <select
              data-testid="data-entry-month"
              value={month}
              onChange={e => setPeriod(`${year}-${e.target.value}`)}
              className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]"
            >
              {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Année</label>
            <select
              data-testid="data-entry-year"
              value={year}
              onChange={e => setPeriod(`${e.target.value}-${month}`)}
              className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]"
            >
              <option value="2025">2025</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
            </select>
          </div>
        </div>

        {/* Show current establishment info */}
        {selectedEstObj && (
          <div className="mb-6 px-4 py-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-6 text-xs text-gray-500">
            <span className="font-bold text-[#1B2A4A]">{selectedEstObj.name}</span>
            {selectedEstObj.city && <span>{selectedEstObj.city}</span>}
            {selectedEstObj.sector_name && <span className="text-[#B8860B]">{selectedEstObj.sector_name}</span>}
            {selectedEstObj.zone && <span>{selectedEstObj.zone}</span>}
            {selectedEstObj.is_demo && <span className="px-1.5 py-0.5 bg-gray-200 text-gray-500 rounded text-[9px] font-bold uppercase">Cohorte</span>}
            {isPharmacy && selectedEstObj.employees_fte && <span>ETP : {selectedEstObj.employees_fte}</span>}
            {isPharmacy && !selectedEstObj.employees_fte && <span className="text-red-500 font-bold">ETP manquant (obligatoire)</span>}
          </div>
        )}

        <div className="space-y-5">
          {FIELDS.reduce((acc, f) => {
            if (f.group) {
              acc.push({ group: f, fields: [] });
            } else if (acc.length > 0) {
              acc[acc.length - 1].fields.push(f);
            }
            return acc;
          }, []).map((section, i) => (
            <div key={i}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-[10px] font-bold text-[#1B2A4A] uppercase tracking-wider whitespace-nowrap">{section.group.label}</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {section.fields.map(field => (
                  <div key={field.key} className={field.indent ? 'pl-3 border-l-2 border-[#B8860B]/20' : ''}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      {field.indent && <span className="text-[#B8860B] mr-1">&rsaquo;</span>}
                      {field.label} {field.unit && <span className="text-[#B8860B]">({field.unit})</span>}
                      {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      data-testid={`data-entry-${field.key}`}
                      type="number"
                      value={formData[field.key] || ''}
                      onChange={e => handleChange(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          {saved ? (
            <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium" data-testid="data-saved-msg">
              <CheckCircle size={16} /> Données enregistrées
            </div>
          ) : (
            <div className="text-xs text-gray-400">Tous les montants sont HT</div>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !selectedEst}
            className="bg-[#1B2A4A] text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#152039] transition-all shadow-md disabled:opacity-50 flex items-center gap-2"
            data-testid="data-entry-save-btn"
          >
            {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={16} />}
            Enregistrer
          </button>
        </div>
      </div>

      {existingData.length > 0 && (
        <div className="bg-white rounded-xl shadow-[0_2px_8px_rgba(27,42,74,0.04)] border border-gray-100 p-6">
          <h2 className="font-bold text-[#1B2A4A] font-manrope mb-4">Historique des saisies</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Période</th>
                  <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">CA HT</th>
                  {isPharmacy ? (
                    <>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Achats</th>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Personnel</th>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Ordonnances</th>
                    </>
                  ) : (
                    <>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Clients</th>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Matières</th>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Personnel</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {existingData.map(d => (
                  <tr key={d.period} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2.5 px-3 font-medium text-[#1B2A4A]">{d.period}</td>
                    <td className="py-2.5 px-3 text-right">{Number(d.data.ca_ht).toLocaleString('fr-FR')} €</td>
                    {isPharmacy ? (
                      <>
                        <td className="py-2.5 px-3 text-right">{Number(d.data.achats_marchandises || 0).toLocaleString('fr-FR')} €</td>
                        <td className="py-2.5 px-3 text-right">{Number(d.data.cout_personnel || 0).toLocaleString('fr-FR')} €</td>
                        <td className="py-2.5 px-3 text-right">{Number(d.data.nb_ordonnances || 0).toLocaleString('fr-FR')}</td>
                      </>
                    ) : (
                      <>
                        <td className="py-2.5 px-3 text-right">{Number(d.data.nb_clients).toLocaleString('fr-FR')}</td>
                        <td className="py-2.5 px-3 text-right">{Number(d.data.cout_matieres).toLocaleString('fr-FR')} €</td>
                        <td className="py-2.5 px-3 text-right">{Number(d.data.cout_personnel).toLocaleString('fr-FR')} €</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
